/**
 * Pack export dialog (Specs/submission-pack-spec.md, P4 / Task D3).
 *
 * Two steps, and the first one only when it has something to say:
 *
 *  1. BLOCKERS — if anything is unresolved the dialog says so before the pack
 *     leaves the building. It **warns, never blocks** (spec P4): "Export anyway"
 *     is the primary action and always available. "Review blockers" is the way
 *     out — it closes the dialog and scrolls the band into view.
 *  2. TEMPLATE — the org's default is pre-selected (starred), with a plain
 *     option for a pack with no Word template at all. The `ArtifactDetailPage`
 *     convention, moved into a dialog because the pack export has a confirm
 *     step in front of it and a split button has nowhere to put one.
 *
 * The run itself lives in the store so the header button can show progress
 * after this dialog has closed — a multi-document zip is not a modal's worth of
 * waiting.
 */

import { useEffect, useState } from 'react'
import { AlertTriangle, Star } from 'lucide-react'
import { toast } from 'sonner'
import { packNoticeText } from '@/lib/pending-ai-change-notices'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { packBlockerLabels } from '@/components/pack/PackBlockers'
import { routeTemplateExportError, useExportTemplatesStore } from '@/stores/export-templates-store'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import type { ExportOptions, SubmissionPackBlockers } from '@/lib/api'
import { PACK_PREPARATION_COPY } from '@/lib/pack-readiness'

/** Sentinel for "no Word template" — Radix Select forbids an empty value. */
const PLAIN_VALUE = 'plain'

interface PackExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  blockers: SubmissionPackBlockers
  /** Closes the dialog and scrolls the blockers band into view. */
  onReviewBlockers: () => void
}

export function PackExportDialog({
  open,
  onOpenChange,
  blockers,
  onReviewBlockers,
}: PackExportDialogProps) {
  const templates = useExportTemplatesStore((s) => s.templates)
  const fetchTemplates = useExportTemplatesStore((s) => s.fetchTemplates)
  const exportPack = useSubmissionPackStore((s) => s.exportPack)

  const blockerLabels = packBlockerLabels(blockers)
  const [confirmed, setConfirmed] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)

  // Lazy — the templates list is only needed once the dialog is actually open.
  useEffect(() => {
    if (open) void fetchTemplates()
  }, [open, fetchTemplates])

  // A fresh open starts at the blockers again: they may have changed, and a
  // sticky "already confirmed" would silently skip the warning next time.
  useEffect(() => {
    if (!open) {
      setConfirmed(false)
      setSelected(null)
    }
  }, [open])

  const defaultTemplate = templates.find((t) => t.isDefault)
  // `selected` stays null until the user picks, so a template list that arrives
  // after the dialog opened still pre-selects the org default.
  const value = selected ?? defaultTemplate?.id ?? PLAIN_VALUE
  const showBlockers = blockerLabels.length > 0 && !confirmed

  const runExport = (templateId: string | undefined, options?: ExportOptions) => {
    void exportPack(templateId, options)
      .then(({ missingCount, pendingAiChanges }) => {
        if (missingCount > 0) {
          toast.info(
            `Exported with ${missingCount} missing ${
              missingCount === 1 ? 'item' : 'items'
            } — see MANIFEST.xlsx`
          )
        } else if (pendingAiChanges.length === 0) {
          toast.success('Submission pack exported')
        }
        // R0-A: documents that went out with last accepted text because an AI
        // change in them still awaits Accept/Reject.
        const pendingNotice = packNoticeText(pendingAiChanges)
        if (pendingNotice) toast.warning(pendingNotice, { duration: 12_000 })
      })
      .catch((error: unknown) => {
        // A template that would replace part of itself asks first (R3 D4):
        // "Export anyway" resends with the acknowledgement.
        if (routeTemplateExportError(error, () => runExport(templateId, { acknowledgeTemplateLoss: true }))) return
        console.error('[SubmissionPack] Pack export failed', error)
        toast.error(
          error instanceof Error ? error.message : 'Could not export the submission pack'
        )
      })
  }

  const handleExport = () => {
    const templateId = value === PLAIN_VALUE ? undefined : value
    onOpenChange(false)
    runExport(templateId)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {showBlockers ? (
          <>
            <DialogHeader>
              <DialogTitle>Blockers still open</DialogTitle>
              <DialogDescription>{PACK_PREPARATION_COPY.exportBlockersNote}</DialogDescription>
            </DialogHeader>

            <ul className="max-h-[45vh] space-y-1.5 overflow-y-auto">
              {blockerLabels.map(({ key, label }) => (
                <li key={key} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertTriangle
                    aria-hidden
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[hsl(var(--status-warning))]"
                  />
                  <span>{label}</span>
                </li>
              ))}
            </ul>

            <DialogFooter>
              <Button variant="ghost" onClick={onReviewBlockers}>
                Review blockers
              </Button>
              <Button onClick={() => setConfirmed(true)}>Export anyway</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Export draft pack</DialogTitle>
              <DialogDescription>
                A working copy built from the current text: every linked response, workbook and
                attachment, zipped with a manifest of what is in it. To send the kept files, use
                Download files under Files to send.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-1.5">
              <label htmlFor="pack-export-template" className="text-meta">
                Word template
              </label>
              <Select value={value} onValueChange={setSelected}>
                <SelectTrigger id="pack-export-template" aria-label="Word template">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PLAIN_VALUE}>Plain (no template)</SelectItem>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <span className="flex items-center gap-2">
                        <span className="truncate">{template.name}</span>
                        {template.isDefault && (
                          <Star
                            aria-label="Default template"
                            className="h-3 w-3 shrink-0 fill-[hsl(var(--gold))] text-[hsl(var(--gold))]"
                          />
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleExport}>Export draft pack</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
